import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, Settings2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { API_BASE as API } from '../lib/apiBase';

/**
 * EU-friendly: essential-only is default; "Accept" records willingness for future optional cookies (same as legal policy today: no ad pixels).
 * Server-side log when logged in. Guests: localStorage only until login.
 */
export function CookieBanner() {
  const { lang } = useLang();
  const { token } = useAuth();
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);

  useEffect(() => {
    const c = localStorage.getItem('cookie_consent');
    if (!c) setShow(true);
  }, []);

  const persist = useCallback(
    async (choice) => {
      setSaving(true);
      const d = new Date().toISOString();
      localStorage.setItem('cookie_consent', choice);
      localStorage.setItem('cookie_consent_date', d);
      localStorage.setItem('cookie_consent_choice', choice);
      if (token) {
        try {
          await fetch(`${API}/api/legal/cookie-consent`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ choice: choice === 'accept_all' ? 'accept_all' : 'essential_only' }),
          });
        } catch {
          /* no bloquea UX: ya guardado en local */
        }
      }
      setSaving(false);
      setShow(false);
      setOpenSettings(false);
    },
    [token]
  );

  if (!show) return null;

  const t = (es, en) => (lang === 'es' ? es : en);

  return (
    <div data-testid="cookie-banner" className="fixed bottom-20 left-4 right-4 md:left-auto md:right-6 md:bottom-24 md:max-w-md z-[60] animate-fade-in-up">
      <div className="bg-pulse-surface border border-pulse-border rounded-2xl p-4 shadow-2xl backdrop-blur-xl">
        <div className="flex items-start gap-3">
          <Cookie size={20} className="text-pulse-primary flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium mb-1">{t('Cookies', 'Cookies')}</p>
            <p className="text-xs text-pulse-muted leading-relaxed">
              {t(
                'Usamos solo cookies necesarias para sesión, idioma y seguridad. Lo demás, solo con tu acuerdo cuando lo tengamos.',
                'We use essential cookies for session, language and security. Optional tools only with your choice when we add them.'
              )}
            </p>
            {openSettings && (
              <p className="text-[10px] text-pulse-muted mt-2">
                {t(
                  '«Aceptar» acepta condiciones y deja claro el uso; «Solo esencial» limita a lo necesario; puedes leer y ajustar más adelante.',
                  '“Accept” confirms the policy. “Essential only” keeps non-essential off until you opt in. You can change this later in settings or help.'
                )}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <button
                data-testid="accept-cookies"
                onClick={() => !saving && persist('accept_all')}
                disabled={saving}
                className="bg-pulse-primary text-black text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-pulse-primary-hover transition-colors disabled:opacity-50"
              >
                {t('Aceptar', 'Accept')}
              </button>
              <button
                type="button"
                onClick={() => !saving && persist('essential_only')}
                disabled={saving}
                className="text-xs text-white border border-pulse-border px-3 py-1.5 rounded-lg hover:bg-pulse-elevated transition-colors"
              >
                {t('Solo esencial', 'Essential only')}
              </button>
              <button
                type="button"
                onClick={() => setOpenSettings((v) => !v)}
                className="text-xs text-pulse-muted flex items-center gap-1 px-1 py-1.5"
              >
                <Settings2 size={14} /> {t('Configurar', 'Options')}
              </button>
              <Link to="/legal?page=cookies" className="text-xs text-pulse-muted hover:text-white transition-colors">
                {t('Política', 'Policy')}
              </Link>
            </div>
          </div>
          <button
            type="button"
            onClick={() => !saving && persist('essential_only')}
            className="text-pulse-muted hover:text-white p-0.5"
            aria-label={t('Cerrar con solo esencial', 'Close, essential only')}
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
